import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ChatDetailPage } from './pages/ChatDetailPage';
import { ContactsPage } from './pages/ContactsPage';
import { InboxPage } from './pages/InboxPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { GuidesPage } from './pages/GuidesPage';
import { ReportsPage } from './pages/ReportsPage';
import { TicketsPage } from './pages/TicketsPage';
import { SignInPage } from './pages/SignInPage';
import { SettingsPage } from './pages/SettingsPage';
import { useAuth } from './hooks/useAuth';
import { usePresence } from './hooks/usePresence';
import { isConfigured, boundDatabaseId, useEmulator } from './lib/firebase';
import { useIdleLogout } from './lib/i18n';
import { useStoredIdleTimeoutMin } from './components/settings/SecurityPanel';
import { auth } from './lib/firebase';
import { signOut } from 'firebase/auth';
import { useCallback, useEffect } from 'react';
import { useTenants } from './tenant/useTenants';
import { TenantPicker } from './tenant/TenantPicker';
import { applyBranding, readSelection, hostForcedTenant } from './tenant/branding';
import { registerPush } from './native/push';
import { NativeBackHandler } from './native/useAndroidBack';
import { AgentGuide } from './components/AgentGuide';

export function App() {
  const { user, claims, loading, availableTenants, activeTenant, signOut: authSignOut } = useAuth();
  usePresence(claims?.tenantCode, user?.uid);
  const onIdleSignOut = useCallback(() => {
    void signOut(auth);
  }, []);
  useIdleLogout(useStoredIdleTimeoutMin(), onIdleSignOut);

  // Selection is "made" when a persisted tenant is among the allowed list (or
  // there's exactly one tenant, where no choice is needed).
  const persistedCode = readSelection()?.code;
  // A tenant-specific host (e.g. cs.bekesher.net) pins the workspace and skips
  // the picker, even for a multi-tenant admin. activeTenant (useAuth pickActive)
  // resolves to the same forced tenant, so Firestore binds correctly.
  const forcedCode = hostForcedTenant();
  const forcedOk = !!forcedCode && availableTenants.includes(forcedCode);
  const hasSelection =
    forcedOk ||
    availableTenants.length === 1 ||
    (!!persistedCode && availableTenants.includes(persistedCode));
  const needsPicker = availableTenants.length > 1 && !hasSelection;

  // Firestore bound to the wrong named DB for this session (the db singleton
  // binds at import from localStorage; a fresh single-tenant login has nothing
  // persisted yet, so it falls back to env/'(default)'). When that happens we
  // persist the active tenant and reload ONCE to rebind — guarded per-target
  // via sessionStorage so a storage failure can't loop.
  // Under the emulator Firestore is always bound to '(default)' (no named DBs),
  // so skip the prod rebind-to-named-DB dance entirely — otherwise the app would
  // reload-loop trying to bind a 'bekesher' DB that doesn't exist in the emulator.
  const needsRebind =
    !useEmulator && !needsPicker && !!activeTenant && boundDatabaseId !== activeTenant;

  // Enrich tenant branding once we're authenticated and have a settled tenant.
  const { tenants, loading: tenantsLoading } = useTenants(
    !!user && availableTenants.length > 0,
    availableTenants
  );

  // Apply branding + (rebind Firestore if needed) + register push once settled.
  useEffect(() => {
    if (!user || needsPicker || !activeTenant) return;
    const t = tenants.find((x) => x.code === activeTenant) ?? {
      code: activeTenant,
      name: activeTenant,
    };
    applyBranding(t); // persists selection (databaseId === activeTenant)
    if (!useEmulator && boundDatabaseId !== activeTenant) {
      const REBIND_KEY = 'chatolam.rebindTarget';
      if (sessionStorage.getItem(REBIND_KEY) !== activeTenant) {
        sessionStorage.setItem(REBIND_KEY, activeTenant);
        window.location.reload();
      }
      return; // don't register push against a DB we're about to leave
    }
    try {
      sessionStorage.removeItem('chatolam.rebindTarget');
    } catch {
      /* ignore */
    }
    void registerPush(activeTenant);
  }, [user, needsPicker, activeTenant, tenants]);

  if (!isConfigured) return <DemoModePage />;

  if (loading) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Loading…</div>;
  }
  if (!user) return <SignInPage />;
  if (!claims?.role || availableTenants.length === 0) {
    return (
      <div style={{ padding: 24, maxWidth: 420 }}>
        <h2>No access</h2>
        <p style={{ color: '#6b7280' }}>
          Your account ({user.email}) is not provisioned for ChatOlam. Ask your admin to grant a
          role and tenant, or sign in with a different account.
        </p>
        <button
          type="button"
          onClick={() => void authSignOut()}
          style={{
            marginTop: 8,
            padding: '10px 16px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            background: 'white',
            color: '#374151',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Sign out / use another account
        </button>
      </div>
    );
  }

  if (needsPicker) {
    return <TenantPicker tenants={tenants} loading={tenantsLoading} />;
  }

  // Rebinding Firestore to the active tenant's named DB (one-shot reload in the
  // effect above). Don't mount the inbox against the wrong database meanwhile.
  if (needsRebind) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Loading…</div>;
  }

  return (
    <BrowserRouter>
      <NativeBackHandler />
      {/* First-time agent walkthrough — self-shows once per device on entry,
          reopenable via its floating "?" button. */}
      <AgentGuide />
      <Routes>
        <Route path="/" element={<Navigate to="/inbox" replace />} />
        <Route path="/inbox" element={<InboxPage />}>
          <Route path=":chatId" element={<ChatDetailPage />} />
        </Route>
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/onboard" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/inbox" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function DemoModePage() {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f9fafb',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          background: 'white',
          padding: 32,
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: 22 }}>ChatOlam Agent — Preview</h1>
        <p style={{ color: '#6b7280', fontSize: 14 }}>
          Firebase isn’t configured for this build, so live data and sign-in are disabled.
          You’re seeing the app shell only. To run the full UI, set the Vite env vars in
          <code style={{ background: '#f3f4f6', padding: '0 4px', borderRadius: 4, marginLeft: 4 }}>
            agent-ui/.env.local
          </code>
          :
        </p>
        <pre
          style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 16,
            borderRadius: 8,
            fontSize: 12,
            overflowX: 'auto',
          }}
        >{`VITE_FIREBASE_API_KEY=...
VITE_FIREBASE_AUTH_DOMAIN=olami-os.firebaseapp.com
VITE_FIREBASE_PROJECT_ID=olami-os
VITE_FIREBASE_APP_ID=...
VITE_FIRESTORE_DATABASE_ID=bekesher
VITE_API_BASE=https://us-central1-olami-os.cloudfunctions.net/chatOlam`}</pre>
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          Then run <code>npm run dev</code> from <code>packages/chatolam/agent-ui</code>.
        </p>
        <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Routes shipped</h2>
        <ul style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
          <li><code>/inbox</code> — agent inbox list + chat detail</li>
          <li>
            <code>/settings/{`{`}inboxes|teams|labels|canned|macros|automations|sla|bots|webhooks|api-tokens|audit{`}`}</code>
          </li>
          <li><code>/reports</code> — conversation volume / agent perf / CSAT</li>
        </ul>
      </div>
    </div>
  );
}
